import { CSSProperties, ReactNode, useMemo, useState } from "react";
import { Dnd } from "../Dnd";
import { Flex, FlexProps } from "../Flex";
import { useDroppable } from "@dnd-kit/core";
import { calculateValidity } from "./internal/utils";
import {
  horizontalListSortingStrategy,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { GapSize } from "../../types";
import { DndSortZoneAdvancedRenderer } from "./types";
import { sizeToToken } from "../../internal/functions";
import DndSortZoneContextProvider from "./internal/DndSortZoneContextProvider";

const DEFAULT_GAP: GapSize = 4;

/**
 * Common properties shared by all zone types
 */
type CommonZoneProps = {
  /**
   * Array of accepted types for the drop zone. Items with matching types can be dropped here.
   */
  acceptedTypes?: string[];
  /**
   * The content to be rendered inside the drop zone.
   */
  children?: ReactNode;
  /**
   * Whether the drop zone is disabled and cannot accept drops.
   */
  disabled?: boolean;
  /**
   * Unique identifier for the drop zone.
   */
  id: string | number;
  /**
   * Label for the drop zone, used for accessibility.
   */
  label: string;
  /**
   * Function to render the drop zone using internal state parameters.
   */
  advancedRenderer?: DndSortZoneAdvancedRenderer;
  /**
   * Function to validate the drop action. Returns true for valid drops, false for invalid drops.
   */
  validator?: (id: string | number) => boolean;
};

/**
 * Properties for sortable zones that can reorder items
 */
type SortableZoneProps = CommonZoneProps & {
  /**
   * Indicates this zone can sort and reorder items.
   */
  sortable: true;
  /**
   * Array of sorted IDs for the items in the zone, must match render order.
   */
  sortedIds: (string | number)[];
  /**
   * Determines where an item will be placed when dropped loosely in a zone.
   * @default "end"
   */
  defaultDropPosition?: "start" | "end";
  /**
   * Gap size between items in the drop zone, necessary for the placement of the drop line.
   * Must reserve space for the drop line.
   */
  gap?: Exclude<GapSize, 0 | "0" | "half">;
  /**
   * Orientation of the items in the drop zone.
   */
  orientation: "horizontal" | "vertical";
};

/**
 * Properties for non-sortable zones that only accept drops
 */
type NonSortableZoneProps = CommonZoneProps & {
  /**
   * Indicates this zone cannot sort items.
   * @default false
   */
  sortable?: false;
  /**
   * Not available for non-sortable zones.
   */
  sortedIds?: never;
  /**
   * Not available for non-sortable zones.
   */
  defaultDropPosition?: never;
  /**
   * Gap size between items in the drop zone.
   */
  gap?: GapSize;
  /**
   * Orientation of the items in the drop zone.
   */
  orientation?: "horizontal" | "vertical";
};

/**
 * Props for the DndSortZone component
 */
export type DndSortZoneProps = SortableZoneProps | NonSortableZoneProps;

/**
 * Props for the DndSortZone component with Flex layout support
 * @extends Omit<FlexProps, "id" | "gap">
 * @extends DndSortZoneProps
 */
export type DndSortZonePropsWithFlexLayout = Omit<FlexProps, "id" | "gap"> &
  DndSortZoneProps;

/**
 * A drop zone component for drag and drop sorting functionality.
 *
 * Features:
 * - Configurable as sortable or non-sortable zones
 * - Type-based validation for accepted items
 * - Custom validation functions
 * - Visual feedback during drag operations
 * - Flexible layout with horizontal/vertical orientation
 * - Advanced rendering with state parameters
 * - Accessible with proper ARIA attributes
 * - Extends Flex component functionality
 * - Automatic drop line positioning
 * - Support for custom gap sizing
 *
 * @example
 * <DndSortZone
 *   id="zone-1"
 *   label="Sortable Zone"
 *   sortable={true}
 *   sortedIds={["item-1", "item-2"]}
 *   orientation="vertical"
 *   acceptedTypes={["card"]}
 * >
 *   <DndSortCard id="item-1" label="Item 1">Content</DndSortCard>
 *   <DndSortCard id="item-2" label="Item 2">Content</DndSortCard>
 * </DndSortZone>
 */
export default function DndSortZone(props: DndSortZonePropsWithFlexLayout) {
  const {
    acceptedTypes,
    children,
    defaultDropPosition,
    gap,
    id,
    label,
    orientation,
    advancedRenderer,
    sortedIds,
    sortable,
    validator,
    style,
    ...flexProps
  } = {
    defaultDropPosition: "end",
    gap: DEFAULT_GAP,
    sortable: false,
    sortedIds: [],
    ...props,
  };
  const [previouslyDragging, setPreviouslyDragging] = useState(false);

  const { setNodeRef, isOver, active, over } = useDroppable({
    id,
    data: {
      acceptedTypes,
      defaultDropPosition,
      label,
      orientation,
      sortedIds,
      sortable,
      validator,
      isZone: true,
    },
  });

  const isDragging = !!active; // Check if any item is currently being dragged
  const isOverThis = !!isOver || over?.data.current?.zone?.id === id; // Check if the current drop target is this zone
  const isValid = useMemo(
    () =>
      calculateValidity({
        active,
        acceptedTypes,
        validator,
      }),
    [acceptedTypes, active, validator],
  );

  const advancedRender = useMemo(() => {
    /* This strange logic fixes an edge case where conditionally rendered items are not correctly rendered in the preview overlay.
    By ensuring that the advanced renderer renders the initial state once before rendering the actual state, we give items a chance to update the preview. */
    if (!previouslyDragging && isDragging) {
      setPreviouslyDragging(true);
      return props?.advancedRenderer?.({
        isDragging: false,
        isOver: false,
        isValid: true,
      });
    } else {
      if (previouslyDragging && !isDragging) {
        setPreviouslyDragging(false);
      }
      return props?.advancedRenderer?.({
        isDragging,
        isOver: isOverThis,
        isValid,
      });
    }
  }, [isDragging, isOverThis, isValid, props, previouslyDragging]);

  const orientationToDirection: Record<string, CSSProperties["flexDirection"]> =
    {
      horizontal: "row",
      vertical: "column",
    };

  const shared = (
    <Dnd.Zone
      el={Flex}
      ref={setNodeRef}
      data-testid={`drag-and-drop-zone-${id}`}
      {...flexProps}
      gap={gap}
      id={id}
      orientation={orientation}
      isDragging={!!active}
      isOver={isOverThis} // Check if the current over target is the same as this zone
      isValid={isValid}
      style={{
        "--drop-zone-gap": sizeToToken(gap),
        padding: sizeToToken(gap),
        flexDirection: orientationToDirection[orientation ?? ""] || undefined,
        ...style,
      }}
    >
      <DndSortZoneContextProvider sortable={sortable} {...props}>
        {advancedRender || children}
      </DndSortZoneContextProvider>
    </Dnd.Zone>
  );

  return sortable ? (
    <SortableContext
      id={`sort-${id}`}
      items={sortedIds.map((id) => ({ id }))}
      strategy={
        orientation === "horizontal"
          ? horizontalListSortingStrategy
          : verticalListSortingStrategy
      }
    >
      {shared}
    </SortableContext>
  ) : (
    shared
  );
}
